import { TurnsGame } from '../../games/turns/TurnsGame.js';
import { TurnsVortex } from '../../games/turns/TurnsVortex.js';
import { TurnsRenderer } from '../../games/turns/TurnsRenderer.js';
import { Grid } from '../../core/Grid.js';
import { Position, positionKey } from '../../core/GridGeometry.js';
import { RenderConfig } from '../../rendering/Renderer.js';
import { TutorialGameAdapter } from '../TutorialEngine.js';

// ── Turns-specific types ──────────────────────────────────────────

export interface TurnsRenderOptions {
    /**
     * Show the click-a-digit path aid for this digit: the drive segments leaving each of
     * its crossings, highlighted in blue. Ignored when `selectStart` is set.
     */
    selectDigit?: number;
    /** Show the same aid for the start corner (the drive's opening segment). */
    selectStart?: boolean;
}

/**
 * The game exposes selection as *toggles* (that's what a click does), but a scene declares
 * the selection it wants and renders repeatedly — every animation frame — so driving the
 * toggles blindly would flip the highlight on and off. Set it idempotently.
 */
function applySelection(game: TurnsGame, options: TurnsRenderOptions | undefined): void {
    const wantStart = options?.selectStart ?? false;
    const wantDigit = wantStart ? null : options?.selectDigit ?? null;

    if (game.isStartSelected() !== wantStart) game.toggleSelectedStart();
    if (wantStart) return;

    const current = game.getSelectedDigit();
    if (current === wantDigit) return;
    if (current !== null) game.toggleSelectedDigit(current);   // clear the old one
    if (wantDigit !== null) game.toggleSelectedDigit(wantDigit);
}

// ── Adapter ───────────────────────────────────────────────────────

export class TurnsTutorialAdapter implements TutorialGameAdapter {
    private game: TurnsGame;
    private renderer: TurnsRenderer;

    constructor(canvas: HTMLCanvasElement, grid: Grid) {
        this.game = new TurnsGame(grid);
        this.renderer = new TurnsRenderer(canvas, grid);
        // Static scenes: no tweening, so the renderer never self-re-renders and
        // wipes the engine-drawn annotations.
        this.renderer.setSelfAnimation(false);
    }

    applyState(state: unknown): void {
        const serialized = state as string;
        this.game.reset();
        if (serialized) {
            this.game.deserialize(serialized);
        }
    }

    render(options: unknown): void {
        applySelection(this.game, options as TurnsRenderOptions | undefined);
        this.renderer.render(this.game);
    }

    getConfig(): RenderConfig {
        return this.renderer.getConfig();
    }
}

export function createTurnsTutorialAdapter(
    canvas: HTMLCanvasElement,
    grid: Grid,
): TutorialGameAdapter {
    return new TurnsTutorialAdapter(canvas, grid);
}

// ── Vortex state ─────────────────────────────────────────────────

export interface TurnsVortexTutorialState {
    /** Serialized turns, optionally with the `|`-suffixed active-gate list. */
    turns: string;
    gateMode?: boolean;
    /**
     * Extra gate pairs (cell strings, e.g. ['B5', 'E5']) added on top of the adapter's
     * default teleport pair for this scene only. Used by the activation scene to show a
     * second, blocked (amber) pair alongside the main one. Omit to use just the default.
     */
    extraGates?: Array<[string, string]>;
}

/** Parse a cell string like "B5" into a Position ({ row: 5, col: 1 }). */
function parseCellString(s: string): Position {
    return { col: s.charCodeAt(0) - 65, row: parseInt(s.slice(1), 10) };
}

// ── Vortex Adapter ───────────────────────────────────────────────

export class TurnsVortexTutorialAdapter implements TutorialGameAdapter {
    private game: TurnsVortex;
    private renderer: TurnsRenderer;
    private gatePairs: Array<[Position, Position]>;
    private currentGateMode: boolean = false;

    constructor(
        canvas: HTMLCanvasElement,
        grid: Grid,
        gatePairs: Array<[Position, Position]>,
    ) {
        this.game = new TurnsVortex(grid);
        this.renderer = new TurnsRenderer(canvas, grid);
        this.renderer.setSelfAnimation(false);   // static scenes — see the Base adapter
        this.gatePairs = gatePairs;
    }

    applyState(state: unknown): void {
        const s = state as TurnsVortexTutorialState | null;
        this.game.reset();

        const pairs = [...this.gatePairs];
        if (s?.extraGates) {
            for (const [a, b] of s.extraGates) {
                pairs.push([parseCellString(a), parseCellString(b)]);
            }
        }
        const allGates = new Set<string>();
        for (const [a, b] of pairs) {
            allGates.add(positionKey(a));
            allGates.add(positionKey(b));
        }
        this.game.setVortexData(pairs, allGates);

        if (s?.turns) {
            this.game.deserialize(s.turns);
        }
        this.currentGateMode = s?.gateMode ?? false;
    }

    render(options: unknown): void {
        // TurnsRenderer already draws active gates and, in gate mode, the placement
        // highlights — so the Vortex tutorial needs no renderer subclass of its own.
        this.renderer.setGateMode(this.currentGateMode);
        applySelection(this.game, options as TurnsRenderOptions | undefined);
        this.renderer.render(this.game);
    }

    getConfig(): RenderConfig {
        return this.renderer.getConfig();
    }
}

export function createTurnsVortexTutorialAdapter(
    canvas: HTMLCanvasElement,
    grid: Grid,
): TutorialGameAdapter {
    // Gate pair for the Vortex Turns tutorial: B2 ↔ E3. It is what carries the cart into
    // row 3, where digit 2 sits — without it, digit 2 is unreachable on this grid.
    const gatePairs: Array<[Position, Position]> = [
        [{ row: 2, col: 1 }, { row: 3, col: 4 }],
    ];
    return new TurnsVortexTutorialAdapter(canvas, grid, gatePairs);
}
